/**
 * Single source of truth for Pathfinder map / device-tree colors.
 *
 * Historically this lived in three different places (PathfinderPage's
 * `studentColors.getColor`, PathfinderPage's `getCoTravelerRouteColor`, and
 * HierarchicalDeviceTree's `getEntityColor` + `getDeviceTypeColor`) with
 * subtly different palettes. The cotraveler version intentionally inverted
 * the device-type mapping so cotraveler tracks didn't paint the same hue as
 * the primary; the side-effect was that "purple line on the map" meant
 * `vehicle` for the primary entity but `phone` for a Student cotraveler,
 * and the legend stopped meaning one thing.
 *
 * This module collapses all of that into a single `resolvePathfinderColor`
 * function with a strict priority order so a given (entity_type, device_type,
 * device_id) tuple paints the same hue everywhere it appears.
 *
 * Priority (first match wins):
 *   1. Per-device user override (set via the color-picker in the device tree)
 *   2. Entity-type override:
 *        SRP       → red       (gov-mandated)
 *        RP        → orange    (gov-mandated; "amber" colloquially)
 *        WIFI      → green     (matches phone — RF-cotraveler signal class)
 *        BLUETOOTH → blue      (matches watch)
 *        CELLULAR  → amber     (yellow-amber, distinct from RP orange)
 *   3. SRP/RP token detected inside `device_id` (defensive fallback when
 *      entity_type isn't passed — matches as a discrete token to avoid
 *      catching "PuRPle" inside arbitrary names)
 *   4. Device-type:
 *        phone     → green
 *        watch     → blue
 *        vehicle   → purple
 *
 * Cotraveler tracks distinguish themselves from the primary via dashed line
 * style + reduced opacity (set in PathfinderPage when cotraveler points/routes
 * are merged in), NOT via a different hue. That keeps the legend consistent.
 */

export const ENTITY_TYPE_COLORS = {
  SRP: '#f44336',
  RP: '#ff9800',
  WIFI: '#4caf50',
  BLUETOOTH: '#2196f3',
  CELLULAR: '#ffc107',
  ADTECH: '#e91e63', // pink — Venntel/GeoNeutron commercial mobile-location
} as const;

export const DEVICE_TYPE_COLORS = {
  phone: '#4caf50',
  watch: '#2196f3',
  vehicle: '#9c27b0',
  wardriver: '#00838f',
} as const;

export const DEFAULT_COLOR = '#757575';

/**
 * Distinct hues for AdTech registration ids. Commercial PoL loads many
 * devices that all share deviceType=phone; without per-id coloring they
 * all paint the same green. Palette is cycled via a stable hash of the
 * registration id so a device keeps its color across reloads.
 */
export const ADTECH_DEVICE_PALETTE = [
  '#e91e63', // pink
  '#9c27b0', // purple
  '#673ab7', // deep purple
  '#3f51b5', // indigo
  '#2196f3', // blue
  '#03a9f4', // light blue
  '#00bcd4', // cyan
  '#009688', // teal
  '#4caf50', // green
  '#8bc34a', // light green
  '#cddc39', // lime
  '#ffc107', // amber
  '#ff9800', // orange
  '#ff5722', // deep orange
  '#795548', // brown
  '#607d8b', // blue grey
  '#f06292', // pink 200
  '#ba68c8', // purple 200
  '#7986cb', // indigo 200
  '#4dd0e1', // cyan 200
  '#81c784', // green 200
  '#ffb74d', // orange 200
  '#a1887f', // brown 200
  '#90a4ae', // blue grey 200
] as const;

/** Stable (not session-random) color for an AdTech registration id. */
export function colorForAdtechId(registrationId: string | null | undefined): string {
  const id = (registrationId || '')
    .replace(/^GN-/i, '')
    .replace(/-(phone|watch|vehicle)$/i, '')
    .trim();
  if (!id) return ENTITY_TYPE_COLORS.ADTECH;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % ADTECH_DEVICE_PALETTE.length;
  return ADTECH_DEVICE_PALETTE[idx];
}

export type DeviceTypeKey = keyof typeof DEVICE_TYPE_COLORS;
export type EntityTypeKey = keyof typeof ENTITY_TYPE_COLORS;

export interface ResolveColorOpts {
  entityType?: string | null;
  deviceType?: string | null;
  deviceId?: string | null;
  customDeviceColors?: Record<string, string>;
}

/**
 * Resolve the canonical color for a (entity_type, device_type, device_id)
 * tuple. See module-level comment for priority rules.
 */
export function resolvePathfinderColor(opts: ResolveColorOpts): string {
  const { entityType, deviceType, deviceId, customDeviceColors } = opts;

  if (deviceId && customDeviceColors && customDeviceColors[deviceId]) {
    return customDeviceColors[deviceId];
  }

  const et = (entityType || '').toUpperCase();
  if (et === 'SRP') return ENTITY_TYPE_COLORS.SRP;
  if (et === 'RP') return ENTITY_TYPE_COLORS.RP;
  if (et === 'WIFI') return ENTITY_TYPE_COLORS.WIFI;
  if (et === 'BLUETOOTH') return ENTITY_TYPE_COLORS.BLUETOOTH;
  if (et === 'CELLULAR') return ENTITY_TYPE_COLORS.CELLULAR;
  // AdTech: per-registration color so multi-device PoL loads aren't monochrome.
  if (et === 'ADTECH') return colorForAdtechId(deviceId);

  // Defensive fallback — pick up SRP/RP from the device_id when the caller
  // doesn't have an entity_type to pass. Tokenize on dashes/underscores/
  // whitespace so "PurpleHaze" doesn't get flagged as RP.
  if (deviceId) {
    const tokens = deviceId.toUpperCase().split(/[-_\s]+/);
    if (tokens.includes('SRP')) return ENTITY_TYPE_COLORS.SRP;
    if (tokens.includes('RP')) return ENTITY_TYPE_COLORS.RP;
  }

  const dt = (deviceType || '').toLowerCase();
  if (dt === 'phone' || dt === 'cellphone' || dt === 'samsungphone' ||
      dt === 'smartphone' || dt === 'mobile') {
    return DEVICE_TYPE_COLORS.phone;
  }
  if (dt === 'watch' || dt === 'smartwatch' || dt === 'applewatch') {
    return DEVICE_TYPE_COLORS.watch;
  }
  if (dt === 'vehicle' || dt === 'beacon' || dt === 'car') {
    return DEVICE_TYPE_COLORS.vehicle;
  }
  if (dt === 'wardriver' || dt === 'beluga' || dt === 'rf_collector' || dt === 'rf-collector') {
    return DEVICE_TYPE_COLORS.wardriver;
  }

  // Default to phone-green so unclassified GPS still paints something
  // sensible. The previous behavior was identical.
  return DEVICE_TYPE_COLORS.phone;
}

/**
 * Convenience wrapper for callers that only have a device_type. Equivalent to
 * `resolvePathfinderColor({ deviceType })`.
 */
export function getDeviceTypeColor(deviceType: string | null | undefined): string {
  return resolvePathfinderColor({ deviceType });
}
